import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components/native';

import { H5 } from '../../components/Typography';
import Button from '../../components/Button';
import Spacer from '../../components/Spacer';
import { StackNavigationProp } from '@react-navigation/stack';
import {
  RouteProp,
  StackActions,
  useFocusEffect,
  useNavigation,
  useRoute
} from '@react-navigation/native';
import { QualificationParamList } from '../../web-navigation/QualificationNavigator';
import { FormElementMeta } from '@people-science/forms-core';
import { camelCase } from 'lodash';
import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import DynamicForm, {
  DynamicFormBackApi
} from '../../dynamic-forms/DynamicForm';
import {
  useFormNavigation,
  useRegisterFormBack
} from '../../dynamic-forms/navigation/FormNavigationContext';
import {
  appendSectionHistory,
  parseSectionHistory
} from '../../dynamic-forms/navigation/formBack';
import DynamicFormProvider from '../../dynamic-forms/DynamicFormProvider';
import { useGetFormTemplate } from '../../dynamic-forms/hooks/useGetFormTemplate';
import { useRunQuery } from '../../dynamic-forms/hooks/useRunQuery';
import { useUserContext } from '../../context/UserContext';
import { mergeDraftValues } from '../../dynamic-forms/drafts/formDraftStore';
import { useFormDraft } from '../../dynamic-forms/drafts/useFormDraft';
import QualificationFormDataSource from './QualificationFormDataSource';

const StyledFormNotFoundContainerView = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const QualificationFormScreen: React.FC = () => {
  const { user } = useUserContext();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const route =
    useRoute<RouteProp<QualificationParamList, 'QualificationForm'>>();
  const { formTemplateId, studyParticipantId, path, newEntry, fromJoinScreen } =
    route.params || {};
  const entityDataIdRef = useRef(route.params?.entityDataId);
  entityDataIdRef.current = route.params?.entityDataId;
  const history = useMemo(
    () => parseSectionHistory(route.params?.history),
    [route.params?.history]
  );
  // fields to focus on and complete
  let dataSourceFields: string[] = useMemo(
    () =>
      route.params?.dataSourceFields
        ? JSON.parse(route.params.dataSourceFields)
        : [],
    [route.params?.dataSourceFields]
  );
  const { formTemplate, loadingFormTemplate, getFormTemplate } =
    useGetFormTemplate(formTemplateId || '');

  const statePath = useMemo(() => {
    const stateNodes = formTemplate?.config?.states;
    if (dataSourceFields.length === 0 || !stateNodes) return path;
    for (const stateNode of stateNodes) {
      if (!stateNode.meta?.elements) continue;
      if (
        stateNode.meta.elements.some(e =>
          dataSourceFields.includes(e.dataSource || '')
        )
      ) {
        return `/${formTemplate.config?.name}/${stateNode.name}`;
      }
    }
    return path;
  }, [dataSourceFields, formTemplate?.config, path]);
  // Load the participant event form data on mount as well as refresh the data
  // when the screen comes into focus from the route navigation via
  // useFocusEffect to prevent display of previous state.
  useFocusEffect(
    useCallback(() => {
      if (formTemplateId) getFormTemplate();
    }, [getFormTemplate, formTemplateId])
  );

  // run query to get entity data
  const { queryData, isRunningQuery, runQueryGetOne } = useRunQuery();
  // Identify the row by participant and this study's table rather than by an id
  // handed over in navigation: a row id means different records in different
  // studies' tables, so one arriving from another form addresses a stranger's
  // qualification here.
  const loadExisting = Boolean(studyParticipantId) && !newEntry;
  const queryRanRef = useRef(false);
  useEffect(() => {
    if (isRunningQuery) queryRanRef.current = true;
  }, [isRunningQuery]);
  useFocusEffect(
    useCallback(() => {
      if (loadExisting && user?.id && formTemplate?.entity) {
        const entitySchema = formTemplate?.entity;
        const alias = camelCase(entitySchema.name);
        queryRanRef.current = false;
        runQueryGetOne({
          queryType: 'select',
          selects: [],
          mainAlias: {
            type: 'from',
            name: alias,
            tablePath: entitySchema.tableName
          },
          wheres: [
            {
              type: 'simple',
              condition: {
                operator: 'equal',
                parameters: [
                  `${alias}.studyParticipantId`,
                  String(studyParticipantId)
                ]
              }
            }
          ],
          orderBys: [{ propertyPath: `${alias}.created`, order: 'DESC' }]
        });
      }
    }, [
      loadExisting,
      studyParticipantId,
      user?.id,
      formTemplate?.entity,
      runQueryGetOne
    ])
  );

  // load existing data if any
  const [formValues, setFormValues] = useState(queryData);
  useEffect(() => {
    if (queryData) {
      setFormValues(queryData);
    } else {
      setFormValues({});
    }
  }, [queryData]);

  // display spinner while loading elements to prevent flickering
  const [loadingElements, setLoadingElements] = useState(false);

  // Local draft (CD-222): survive an app kill mid-section. Restored on a
  // fresh open only (mid-walk section pushes carry `path`); the server copy
  // always wins per field.
  const draft = useFormDraft({
    userId: user?.id,
    formTemplateId: formTemplate?.id,
    instanceId: route.params?.entityDataId,
    formTemplateVersion: formTemplate?.version,
    formConfig: formTemplate?.config
  });
  const [restoredStatePath, setRestoredStatePath] = useState<string>();
  const [restoredHistory, setRestoredHistory] = useState<string[]>();
  const [draftChecked, setDraftChecked] = useState(false);
  useEffect(() => {
    if (draftChecked || !formTemplate) return;
    if (!draft.enabled || path) {
      setDraftChecked(true);
      return;
    }
    let stale = false;
    draft.restoreDraft().then(payload => {
      if (stale) return;
      if (payload) {
        setFormValues(current =>
          mergeDraftValues(current || {}, payload.formValues)
        );
        if (payload.history) setRestoredHistory(payload.history);
        if (payload.statePath) {
          setRestoredStatePath(payload.statePath);
          // seed the writer so later value-only writes keep the resume point
          draft.saveDraftStatePath(payload.statePath, payload.history);
        }
      }
      setDraftChecked(true);
    });
    return () => {
      stale = true;
    };
  }, [draft, draftChecked, formTemplate, path]);
  // mid-walk sections seed their own path so drafts resume where typed
  useEffect(() => {
    if (draft.enabled && path) draft.saveDraftStatePath(path, history);
  }, [draft, path, history]);
  const backApiRef = useRef<DynamicFormBackApi | undefined>(undefined);
  const { confirmLeave } = useFormNavigation();
  const currentHistory = restoredHistory ?? history;
  const currentPath =
    statePath ??
    restoredStatePath ??
    (formTemplate?.config?.states?.[0]
      ? `/${formTemplate.config.name}/${formTemplate.config.states[0].name}`
      : undefined);
  useFocusEffect(
    useCallback(() => {
      if (!backApiRef.current) return;
      backApiRef.current.syncToPath(statePath ?? restoredStatePath);
      if (draft.enabled && currentPath) {
        draft.saveDraftStatePath(currentPath, currentHistory);
      }
    }, [statePath, restoredStatePath, draft, currentPath, currentHistory])
  );
  useRegisterFormBack(async () => {
    const state = navigation.getState();
    const previous = state.routes[state.index - 1];
    const previousParams = (previous?.params || {}) as {
      formTemplateId?: number | string;
    };
    const sameTemplate =
      previousParams.formTemplateId !== undefined &&
      String(previousParams.formTemplateId) === String(formTemplateId);
    if (previous?.name === route.name && sameTemplate) return false;
    const step = backApiRef.current?.goBack();
    if (step) {
      setRestoredHistory(step.history);
      navigation.setParams({
        path: step.path,
        history: JSON.stringify(step.history)
      });
      return true;
    }
    if (backApiRef.current?.hasUnsavedAnswers()) {
      return !(await confirmLeave());
    }
    return false;
  });

  // the draft gate must not block the no-template path — that would trap a
  // failed template load on the spinner instead of the Not Found fallback
  const readyToDisplay = useMemo(
    () =>
      (formTemplate ? draftChecked : true) &&
      // a participant with no prior row settles with no queryData, so waiting
      // on queryData alone would hang the spinner on a first qualification
      (loadExisting
        ? Boolean(queryData && formValues) ||
          (!isRunningQuery && queryRanRef.current)
        : true),
    [
      draftChecked,
      formTemplate,
      formValues,
      queryData,
      loadExisting,
      isRunningQuery
    ]
  );
  return !readyToDisplay || isRunningQuery || loadingFormTemplate ? (
    <ActivityIndicatorScreen />
  ) : !formTemplateId || !formTemplate ? (
    <StyledFormNotFoundContainerView>
      <H5 color='light'>Qualification Form Not Found</H5>
      <Spacer size={3} />
      <Button
        outlined
        size='small'
        onPress={() => {
          navigation.goBack();
        }}
      >
        Back
      </Button>
    </StyledFormNotFoundContainerView>
  ) : (
    <DynamicFormProvider
      entitySchema={formTemplate?.entity}
      formStateNode={formTemplate?.config}
      onLoadProgress={loadingElementQueue => {
        setLoadingElements(loadingElementQueue.length > 0);
      }}
      onChange={values => {
        draft.saveDraftValues(values);
      }}
    >
      <QualificationFormDataSource
        studyParticipantId={studyParticipantId}
        formTemplate={formTemplate}
        formData={formValues}
        onSubmitted={isFinal => {
          if (isFinal) draft.clearDraft();
        }}
        onCreated={entity => {
          entityDataIdRef.current = entity.id;
          const params = { entityDataId: entity.id } as any;
          if (entity.studyParticipantId)
            params.studyParticipantId = entity.studyParticipantId;
          navigation.setParams(params);
        }}
      >
        <DynamicForm
          values={formValues}
          statePath={statePath ?? restoredStatePath}
          restoredHistory={currentHistory}
          savedValues={queryData}
          backApiRef={backApiRef}
          onBack={() => {
            navigation.pop();
          }}
          onStatePathChange={(
            path,
            stateNode,
            sectionIndex,
            sectionCount,
            stateMachine
          ) => {
            const nextHistory = appendSectionHistory(
              currentHistory,
              currentPath
            );
            // write-through so a kill during the transition resumes here
            draft.saveDraftStatePath(path, nextHistory);
            if (sectionIndex < sectionCount) {
              if (statePath && dataSourceFields.length > 0) {
                const previousStateNode = stateMachine.getStateNode(statePath);
                dataSourceFields = dataSourceFields.filter(d => {
                  if (
                    previousStateNode?.meta?.elements?.some(
                      (e: FormElementMeta<any, any>) => e.dataSource === d
                    )
                  ) {
                    return false; // remove data source field if in stateNode
                  }
                  return true;
                });
                if (dataSourceFields.length === 0) {
                  // navigate to previous screen after completing all
                  // dataSourceFields related questions
                  const navigationState = navigation.getState();
                  let popCount = 0;
                  for (let i = navigationState.routes.length - 1; i >= 0; i--) {
                    if (
                      navigationState.routes[i].name !== 'QualificationForm'
                    ) {
                      navigation.pop(popCount);
                      return;
                    }
                    popCount++;
                  }
                }
              }
              const newSectionIndex = sectionIndex + 1;
              navigation.push('QualificationForm', {
                path,
                entityDataId: entityDataIdRef.current,
                sectionIndex: newSectionIndex,
                formTemplateId,
                studyParticipantId:
                  studyParticipantId || queryData?.studyParticipantId,
                history: JSON.stringify(nextHistory),
                fromJoinScreen: fromJoinScreen
              });
            } else if (sectionIndex >= sectionCount) {
              // NOTE: technically this would never occur (you can't configure
              // a form transition past the last section)
              navigation.dispatch(
                StackActions.replace('QualificationNavigator')
              );
            }
          }}
        />
      </QualificationFormDataSource>
      {loadingElements && (
        <ActivityIndicatorScreen modal backgroundColor='#fff' />
      )}
    </DynamicFormProvider>
  );
};

export default QualificationFormScreen;
